import axios from "axios";
import { AxiosError } from "axios";
import { SpectroInfo } from "@components/geoJS/geoJSUtils";
import type { FeatureCollection, Point } from "geojson";

export interface Recording {
  id: number;
  created: string;
  modified: string;
  name: string;
  audio_file: string;
  audio_file_presigned_url: string;
  owner_id: number;
  owner_username: string;
  recorded_date: string;
  recorded_time: string;
  equipment?: string;
  comments?: string;
  recording_location?: null | GeoJSON.Point;
  grts_cell_id?: null | number;
  sample_frame_id?: null | number;
  public: boolean;
  userMadeAnnotations: boolean;
  userAnnotations: number;
  hasSpectrogram: boolean;
  fileAnnotations: FileAnnotation[];
  site_name?: string;
  software?: string;
  detector?: string;
  species_list?: string;
  unusual_occurrences?: string;
  tags_text?: string[];
}

export type RecordingLocationsFeatureProperties = {
  recording_id: number;
  filename: string;
};

export type RecordingLocationsGeoJson = FeatureCollection<Point, RecordingLocationsFeatureProperties>;

export interface Species {
  species_code: string;
  family: string;
  genus: string;
  common_name: string;
  species_code_6?: string;
  species?: string;
  pk: number;
  category: "single" | "multiple" | "frequency" | "noid";
  in_range?: boolean;
}

export interface SpectrogramAnnotation {
  start_time: number;
  end_time: number;
  low_freq: number;
  high_freq: number;
  id: number;
  editing?: boolean;
  species?: Species[];
  comments?: string;
  type?: string;
  owner_email?: string;
}

export interface SpectrogramSequenceAnnotation {
  start_time: number;
  end_time: number;
  id: number;
  editing?: boolean;
  type?: string;
  comments?: string;
  species?: Species[];
  owner_email?: string;
}

export interface UpdateSpectrogramAnnotation {
  start_time?: number;
  end_time?: number;
  low_freq?: number;
  high_freq?: number;
  id?: number;
  editing?: boolean;
  type?: string;
  species?: Species[];
  comments?: string;
}

export interface UpdateSpectrogramSequenceAnnotation {
  start_time?: number;
  end_time?: number;
  id?: number;
  editing?: boolean;
  species?: Species[];
  comments?: string;
  type?: string;
}

export interface UserInfo {
  username: string;
  email: string;
  id: number;
}

export interface FileAnnotation {
  species: Species[];
  comments?: string;
  model?: string;
  owner: string;
  confidence: number;
  hasDetails: boolean;
  id: number;
  submitted: boolean;
}

export interface FileAnnotationDetails {
  label: string;
  score: number;
  confidences: { label: string; value: string }[];
}
export interface UpdateFileAnnotation {
  recordingId?: number;
  species: number[] | null;
  comments?: string;
  model?: string;
  confidence: number;
  id?: number;
}

export interface Spectrogram {
  urls: string[];
  mask_urls: string[];
  waveplot_urls?: string[];
  filename?: string;
  annotations?: SpectrogramAnnotation[];
  fileAnnotations: FileAnnotation[];
  sequence?: SpectrogramSequenceAnnotation[];
  spectroInfo?: SpectroInfo;
  compressed?: {
    start_times: number[];
    end_times: number[];
  };
  currentUser?: string;
  otherUsers?: UserInfo[];
  /** Present on recording spectrogram payloads when the server includes location context. */
  grts_cell_id?: number | null;
  sample_frame_id?: number | null;
}

export type OtherUserAnnotations = Record<
  string,
  { annotations: SpectrogramAnnotation[]; sequence: SpectrogramSequenceAnnotation[] }
>;

export type UploadLocation = null | { latitude?: number; longitude?: number; gridCellId?: number };

export const axiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_APP_API_ROOT}/api/v1`,
});

export interface RecordingFileParameters {
  name: string;
  recorded_date: string;
  recorded_time: string;
  equipment: string;
  comments: string;
  location?: UploadLocation;
  publicVal: boolean;
  site_name?: string;
  software?: string;
  detector?: string;
  species_list?: string;
  unusual_occurrences?: string;
  tags?: string[];
}

async function uploadRecordingFile(file: File, params: RecordingFileParameters) {
  const formData = new FormData();
  formData.append("audio_file", file);
  formData.append("name", params.name);
  formData.append("recorded_date", params.recorded_date);
  formData.append("recorded_time", params.recorded_time);
  formData.append("equipment", params.equipment);
  formData.append("comments", params.comments);
  if (params.location) {
    if (params.location.latitude && params.location.longitude) {
      formData.append("latitude", params.location.latitude.toString());
      formData.append("longitude", params.location.longitude.toString());
    }
    if (params.location.gridCellId) {
      formData.append("gridCellId", params.location.gridCellId.toString());
    }
  }

  if (params.software) {
    formData.append("software", params.software);
  }
  if (params.detector) {
    formData.append("detector", params.detector);
  }
  if (params.site_name) {
    formData.append("site_name", params.site_name);
  }
  if (params.species_list) {
    formData.append("species_list", params.species_list);
  }
  if (params.unusual_occurrences) {
    formData.append("unusual_occurrences", params.unusual_occurrences);
  }
  if (params.tags) {
    params.tags.forEach((tag: string) => formData.append("tags", tag));
  }
  const recordingParams = {
    name: params.name,
    equipment: params.equipment,
    comments: params.comments,
    site_name: params.site_name,
    software: params.software,
    detector: params.detector,
    species_list: params.species_list,
    unusual_occurrences: params.unusual_occurrences,
    tags: params.tags,
  };
  const payloadBlob = new Blob([JSON.stringify(recordingParams)], { type: "application/json" });
  formData.append("payload", payloadBlob);
  await axiosInstance.post("/recording/", formData, {
    params: { publicVal: !!params.publicVal },
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

async function patchRecording(recordingId: number, params: RecordingFileParameters) {
  const latitude = params.location ? params.location.latitude : undefined;
  const longitude = params.location ? params.location.longitude : undefined;
  const gridCellId = params.location ? params.location.gridCellId : undefined;

  await axiosInstance.patch(
    `/recording/${recordingId}`,
    {
      name: params.name,
      recorded_date: params.recorded_date,
      recorded_time: params.recorded_time,
      equipment: params.equipment,
      comments: params.comments,
      publicVal: !!params.publicVal,
      latitude,
      longitude,
      gridCellId,
      tags: params.tags,
      site_name: params.site_name,
      software: params.software,
      detector: params.detector,
      species_list: params.species_list,
      unusual_occurrences: params.unusual_occurrences,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
}

interface DeletionResponse {
  message?: string;
  error?: string;
}
interface GRTSCellCenter {
  latitude?: number;
  longitude?: number;
  error?: string;
}

interface GRTSCellBbox {
  type: string;
  geometry: {
    type: string;
    coordinates: number[][];
  };
  properties: {
    grts_cell_id: number;
    annotationType: string;
  };
}


/** Params for paginated recording list (v-data-table-server compatible). */
export interface RecordingListParams {
  public?: boolean;
  exclude_submitted?: boolean;
  annotation_completed?: boolean;
  search?: string;
  /** Filter by tags: recording must have all listed tags. Comma-separated or array. */
  tags?: string | string[];
  sort_by?: 'id' | 'name' | 'created' | 'modified' | 'recorded_date' | 'owner_username';
  sort_direction?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  /** WGS84 [minLon, minLat, maxLon, maxLat]; recordings must intersect this box. */
  bbox?: [number, number, number, number];
}

/** Paginated recording list response (v-data-table-server compatible). */
export interface RecordingPaginatedResponse {
  items: Recording[];
  count: number;
}

async function getRecordings(getPublic = false, params?: RecordingListParams) {
  const query = new URLSearchParams();
  if (getPublic) {
    query.set('public', 'true');
  }
  if (params) {
    if (params.public !== undefined) query.set('public', String(params.public));
    if (params.exclude_submitted !== undefined)
      query.set('exclude_submitted', String(params.exclude_submitted));
    if (params.annotation_completed !== undefined)
      query.set('annotation_completed', String(params.annotation_completed));
    if (params.search) query.set('search', params.search);
    if (params.tags !== undefined) {
      const tagStr = Array.isArray(params.tags) ? params.tags.join(',') : params.tags;
      if (tagStr) query.set('tags', tagStr);
    }
    if (params.sort_by) query.set('sort_by', params.sort_by);
    if (params.sort_direction) query.set('sort_direction', params.sort_direction);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (
      params.bbox !== undefined &&
      params.bbox.length === 4 &&
      params.bbox.every((n) => Number.isFinite(n))
    ) {
      query.set('bbox', params.bbox.join(','));
    }
  }
  if (!params?.page) query.set('page', '1');
  if (!params?.limit) query.set('limit', '20');
  return axiosInstance.get<RecordingPaginatedResponse>(`/recording/?${query.toString()}`);
}
async function getRecording(id: string) {
  return axiosInstance.get<Recording>(`/recording/${id}/`);
}

export interface UnsubmittedNeighborsParams {
  sort_by?: 'id' | 'name' | 'created' | 'modified' | 'recorded_date' | 'owner_username';
  sort_direction?: 'asc' | 'desc';
  /** Comma-separated or array of tag texts; recording must have all listed tags. */
  tags?: string | string[];
  /** Bounding box filter (lon/lat) as `[min_lon, min_lat, max_lon, max_lat]`. */
  bbox?: [number, number, number, number];
}

export interface UnsubmittedNeighborsResponse {
  next_id: number | null;
  previous_id: number | null;
}

async function getUnsubmittedNeighbors(
  currentId: number,
  params?: UnsubmittedNeighborsParams
) {
  const query = new URLSearchParams({ current: String(currentId) });
  if (params?.sort_by) query.set('sort_by', params.sort_by);
  if (params?.sort_direction) query.set('sort_direction', params.sort_direction);
  if (params?.tags !== undefined) {
    const tagStr = Array.isArray(params.tags) ? params.tags.join(',') : params.tags;
    if (tagStr) query.set('tags', tagStr);
  }
  if (
    params?.bbox !== undefined &&
    params.bbox.length === 4 &&
    params.bbox.every((n) => Number.isFinite(n))
  ) {
    query.set('bbox', params.bbox.join(','));
  }
  const response = await axiosInstance.get<UnsubmittedNeighborsResponse>(
    `/recording/unsubmitted-neighbors/?${query.toString()}`
  );
  return response;
}

async function getRecordingTags() {
  return axiosInstance.get<string[]>(`/recording-tag/`);
}

async function deleteRecording(id: number) {
  return axiosInstance.delete<DeletionResponse>(`/recording/${id}`);
}

async function getSpectrogram(id: string) {
  return axiosInstance.get<Spectrogram>(`/recording/${id}/spectrogram`);
}

async function getSpectrogramCompressed(id: string) {
  return axiosInstance.get<Spectrogram>(`/recording/${id}/spectrogram/compressed`);
}

async function getAnnotations(recordingId: string) {
  return axiosInstance.get<SpectrogramAnnotation[]>(`/recording/${recordingId}/annotations`);
}

async function getSequenceAnnotations(recordingId: string) {
  return axiosInstance.get<SpectrogramSequenceAnnotation[]>(
    `/recording/${recordingId}/sequence-annotations`
  );
}

async function getSpecies({recordingId, grtsCellId, sampleFrameId}: {recordingId?: number, grtsCellId?: number, sampleFrameId?: number}) {
  return axiosInstance.get<Species[]>("/species/", { params: { recording_id: recordingId, grts_cell_id: grtsCellId, sample_frame_id: sampleFrameId } });
}

async function patchAnnotation(
  recordingId: string,
  annotationId: number,
  annotation: UpdateSpectrogramAnnotation,
  speciesList: number[] | null = null
) {
  return axiosInstance.patch(`/recording/${recordingId}/annotations/${annotationId}`, {
    annotation,
    species_ids: speciesList,
  });
}

async function patchSequenceAnnotation(
  recordingId: string,
  annotationId: number,
  annotation: UpdateSpectrogramSequenceAnnotation,
  speciesList: number[] | null = null
) {
  return axiosInstance.patch(`/recording/${recordingId}/sequence-annotations/${annotationId}`, {
    annotation,
    species_ids: speciesList,
  });
}

async function putAnnotation(
  recordingId: string,
  annotation: UpdateSpectrogramAnnotation,
  speciesList: number[] = []
) {
  return axiosInstance.put<{ message: string; id: number }>(
    `/recording/${recordingId}/annotations`,
    { annotation, species_ids: speciesList }
  );
}

async function putSequenceAnnotation(
  recordingId: string,
  annotation: UpdateSpectrogramSequenceAnnotation,
  speciesList: number[] | null = null
) {
  return axiosInstance.put<{ message: string; id: number }>(
    `/recording/${recordingId}/sequence-annotations`,
    { annotation, species_ids: speciesList }
  );
}

async function deleteAnnotation(recordingId: string, annotationId: number) {
  return axiosInstance.delete<DeletionResponse>(
    `/recording/${recordingId}/annotations/${annotationId}`
  );
}

async function deleteSequenceAnnotation(recordingId: string, annotationId: number) {
  return axiosInstance.delete<DeletionResponse>(
    `/recording/${recordingId}/sequence-annotations/${annotationId}`
  );
}

async function getOtherUserAnnotations(recordingId: string) {
  return axiosInstance.get<OtherUserAnnotations>(
    `/recording/${recordingId}/annotations/other_users`
  );
}

async function getCellLocation(cellId: number, quadrant?: "SW" | "NE" | "NW" | "SE") {
  return axiosInstance.get<GRTSCellCenter>(`/grts/${cellId}`, { params: { quadrant } });
}

async function getCellBbox(cellId: number) {
  return await axiosInstance.get<GRTSCellBbox>(`/grts/${cellId}/bbox`);
}

export interface RecordingLocationsParams {
  exclude_submitted?: boolean;
  /** Comma-separated or array of tag texts; recording must have all listed tags. */
  tags?: string | string[];
  /** Bounding box filter (lon/lat) as `[min_lon, min_lat, max_lon, max_lat]`. */
  bbox?: [number, number, number, number];
}

async function getRecordingLocations(params?: RecordingLocationsParams) {
  const query = new URLSearchParams();
  if (params?.exclude_submitted !== undefined) query.set("exclude_submitted", String(params.exclude_submitted));
  if (params?.tags !== undefined) {
    const tagStr = Array.isArray(params.tags) ? params.tags.join(",") : params.tags;
    if (tagStr) query.set("tags", tagStr);
  }
  if (params?.bbox !== undefined && params.bbox.length === 4 && params.bbox.every((n) => Number.isFinite(n))) {
    query.set("bbox", params.bbox.join(','));
  }
  const qs = query.toString();
  return axiosInstance.get<RecordingLocationsGeoJson>(`/recording-locations/${qs ? `?${qs}` : ""}`);
}

async function getFileAnnotations(recordingId: number) {
  return axiosInstance.get<FileAnnotation[]>(`recording/${recordingId}/recording-annotations`);
}

async function getFileAnnotationDetails(recordingId: number) {
  return axiosInstance.get<FileAnnotation & { details: FileAnnotationDetails }>(
    `recording-annotation/${recordingId}/details`
  );
}

async function putFileAnnotation(fileAnnotation: UpdateFileAnnotation) {
  return axiosInstance.put<{ message: string; id: number }>(`/recording-annotation/`, {
    ...fileAnnotation,
  });
}

async function patchFileAnnotation(fileAnnotationId: number, fileAnnotation: UpdateFileAnnotation) {
  return axiosInstance.patch<{ message: string; id: number }>(
    `/recording-annotation/${fileAnnotationId}`,
    { ...fileAnnotation }
  );
}

async function deleteFileAnnotation(fileAnnotationId: number) {
  return axiosInstance.delete<{ message: string; id: number }>(
    `/recording-annotation/${fileAnnotationId}`
  );
}

async function submitFileAnnotation(fileAnnotationId: number) {
  return axiosInstance.patch<{ id: number, submitted: boolean }>(
    `recording-annotation/${fileAnnotationId}/submit`
  );
}

interface CellIDReponse {
  grid_cell_id?: number;
  error?: string;
}
async function getCellfromLocation(latitude: number, longitude: number) {
  return axiosInstance.get<CellIDReponse>(`/grts/grid_cell_id`, {
    params: { latitude, longitude },
  });
}

export interface ConfigurationSettings {
  display_pulse_annotations: boolean;
  display_sequence_annotations: boolean;
  run_inference_on_upload: boolean;
  spectrogram_x_stretch: number;
  spectrogram_view: "compressed" | "uncompressed";
  is_admin?: boolean;
  default_color_scheme: string;
  default_spectrogram_background_color: string;
  non_admin_upload_enabled: boolean;
  mark_annotations_completed_enabled: boolean;
}

export type Configuration = ConfigurationSettings & { is_admin: boolean };
async function getConfiguration() {
  return axiosInstance.get<Configuration>("/configuration/");
}

async function patchConfiguration(config: ConfigurationSettings) {
  return axiosInstance.patch("/configuration/", { ...config });
}

async function getCurrentUser() {
  return axiosInstance.get<{name: string, email: string, id: number}>("/configuration/me");
}

export interface ProcessingTask {
  id: number;
  created: string;
  modified: string;
  name: string;
  file_items: number[];
  error?: string;
  info?: string;
  status: "Complete" | "Running" | "Error" | "Queued";
  metadata: Record<string, unknown> & { type?: "NABatRecordingProcessing" } & {
    recordingId: string;
  };
  output_metadata: Record<string, unknown>;
}
export interface ProcessingTaskDetails {
  name: string;
  celery_data: {
    state: "PENDING" | "RECEIVED" | "STARTED" | "SUCCESS" | "FAILURE" | "RETRY" | "REVOKED";
    status: ProcessingTask["status"];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    info: Record<string, any>;
    error: string;
  };
}

async function getProcessingTasks(): Promise<ProcessingTask[]> {
  return (await axiosInstance.get("/processing-task")).data;
}

async function getProcessingTaskDetails(taskId: string): Promise<ProcessingTaskDetails> {
  return (await axiosInstance.get(`/processing-task/${taskId}/details`)).data;
}

async function getFilteredProcessingTasks(
  status: ProcessingTask["status"]
): Promise<ProcessingTask[]> {
  return (await axiosInstance.get("/processing-task/filtered/", { params: { status } })).data;
}

async function cancelProcessingTask(taskId: number): Promise<{ detail: string }> {
  return (await axiosInstance.post(`/processing-task/${taskId}/cancel/`)).data;
}

interface GuanoMetadata {
  nabat_grid_cell_grts_id?: string;
  nabat_latitude?: number;
  nabat_longitude?: number;
  nabat_site_name?: string;
  nabat_activation_start_time?: string;
  nabat_activation_end_time?: string;
  nabat_software_type?: string;
  nabat_species_list?: string[];
  nabat_comments?: string;
  nabat_detector_type?: string;
  nabat_unusual_occurrences?: string;
}

async function getGuanoMetadata(file: File): Promise<GuanoMetadata> {
  const formData = new FormData();
  formData.append("audio_file", file);
  const results = await axiosInstance.post<GuanoMetadata>("/guano/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return results.data;
}

export interface ExportStatus {
  id: number;
  status: "pending" | "complete" | "failed";
  downloadUrl?: string;
  created: string;
  expiresAt: string;
}

async function getExportStatus(exportId: number) {
  const result = await axiosInstance.get<ExportStatus>(`/export-annotation/${exportId}`);
  return result.data;
}

export interface VettingDetails {
  id: number;
  user_id: number;
  reference_materials: string;
}

export interface UpdateVettingDetails {
  reference_materials: string;
}

async function getVettingDetailsForUser(userId: number) {
  try {
    const result = await axiosInstance.get<VettingDetails>(`/vetting/user/${userId}`);
    return result.data;
  } catch (err) {
    const error = err as AxiosError;
    if (error.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

async function createOrUpdateVettingDetailsForUser(userId: number, referenceMaterials: string) {
  return (await axiosInstance.post<VettingDetails>(
    `/vetting/user/${userId}`,
    { 'reference_materials': referenceMaterials }
  )).data;
}

export interface Contour {
  curve: number[][];
  level: number;
  index: number;
}

export interface ComputedPulseContour {
  id: number;
  index: number;
  contours: Contour[];
}

async function getComputedPulseContour(recordingId: number) {
  const result = await axiosInstance.get<ComputedPulseContour[]>(`/recording/${recordingId}/pulse_contours`);
  return result.data;
}

/** Batbot slope values (kHz/ms). Keys match backend BatBotSlopes. */
export interface PulseMetadataSlopes {
  slope_at_hi_fc_knee_khz_per_ms?: number;
  slope_at_fc_khz_per_ms?: number;
  slope_at_low_fc_heel_khz_per_ms?: number;
  slope_at_peak_khz_per_ms?: number;
  slope_avg_khz_per_ms?: number;
  slope_hi_avg_khz_per_ms?: number;
  slope_mid_avg_khz_per_ms?: number;
  slope_lo_avg_khz_per_ms?: number;
  slope_box_khz_per_ms?: number;
  slope_hi_box_khz_per_ms?: number;
  slope_lo_box_khz_per_ms?: number;
}

export interface PulseMetadata {
  id: number;
  index: number;
  curve: number[][] | null; // list of [time, frequency]
  char_freq: number[] | null; // point [time, frequency]
  knee: number[] | null; // point [time, frequency]
  heel: number[] | null; // point [time, frequency]
  slopes?: PulseMetadataSlopes | null;
}

async function getPulseMetadata(recordingId: number) {
  const result = await axiosInstance.get<PulseMetadata[]>(`/recording/${recordingId}/pulse_data`);
  return result.data;
}


export {
  uploadRecordingFile,
  getRecordings,
  getRecording,
  patchRecording,
  deleteRecording,
  getSpectrogram,
  getSpectrogramCompressed,
  getSequenceAnnotations,
  getOtherUserAnnotations,
  getSpecies,
  getAnnotations,
  patchAnnotation,
  patchSequenceAnnotation,
  putAnnotation,
  putSequenceAnnotation,
  deleteAnnotation,
  deleteSequenceAnnotation,
  getCellLocation,
  getCellBbox,
  getCellfromLocation,
  getRecordingLocations,
  getGuanoMetadata,
  getFileAnnotations,
  putFileAnnotation,
  patchFileAnnotation,
  deleteFileAnnotation,
  submitFileAnnotation,
  getConfiguration,
  patchConfiguration,
  getProcessingTasks,
  getProcessingTaskDetails,
  cancelProcessingTask,
  getFilteredProcessingTasks,
  getFileAnnotationDetails,
  getExportStatus,
  getRecordingTags,
  getUnsubmittedNeighbors,
  getComputedPulseContour,
  getPulseMetadata,
  getCurrentUser,
  getVettingDetailsForUser,
  createOrUpdateVettingDetailsForUser,
};
